import { useTranslation } from 'react-i18next';

export default function Fleet() {
  const { t } = useTranslation();
  
  // Função para verificar se estamos no período de inverno (novembro a março)
  const isWinterSeason = () => {
    const currentMonth = new Date().getMonth(); // 0 = Janeiro, 10 = Novembro
    return currentMonth >= 10 || currentMonth <= 2; // Novembro (10) até Março (2)
  };

  // Função para aplicar desconto de inverno (30% para rotas em Portugal)
  const applyWinterDiscount = (price: number) => {
    if (isWinterSeason()) {
      return Math.round(price * 0.7); // 30% de desconto para rotas em Portugal
    }
    return price;
  };

  const winterSeason = isWinterSeason();
  
  const fleetCategories = [
    {
      name: t('fleetEconomico'),
      passengers: 3,
      luggage: 3,
      description: t('fleetEconomicoDesc'),
      image: 'https://public.readdy.ai/ai/img_res/cb3ac2511dd2eb0c28387a76dd87aa04.jpg',
      features: [t('fleetFeature1'), t('fleetFeature2'), t('fleetFeature3')],
      price: applyWinterDiscount(35),
      originalPrice: 35,
      example: t('fleetExample1'),
      route: 'Aeroporto Faro - Vilamoura'
    },
    {
      name: t('fleetConforto'),
      passengers: 4,
      luggage: 4,
      description: t('fleetConfortoDesc'),
      image: 'https://readdy.ai/api/search-image?query=Elegant%20mid-size%20sedan%20car%20in%20orange%20and%20gray%20colors%20at%20Algarve%20coastal%20road%2C%20professional%20automotive%20photography%2C%20clean%20white%20background%2C%20luxury%20family%20transfer%20vehicle%2C%20premium%20interior%20visible&width=400&height=300&seq=conforto-car&orientation=landscape',
      features: [t('fleetFeature1'), t('fleetFeature2'), t('fleetFeature4'), t('fleetFeature5')],
      price: applyWinterDiscount(45),
      originalPrice: 45,
      example: t('fleetExample2'),
      route: 'Aeroporto Faro - Albufeira'
    },
    {
      name: t('fleetSUV'),
      passengers: 4,
      luggage: 4,
      description: t('fleetSUVDesc'),
      image: 'https://readdy.ai/api/search-image?query=Luxury%20SUV%20vehicle%20in%20orange%20and%20gray%20colors%20on%20Portuguese%20coastal%20highway%2C%20professional%20automotive%20photography%2C%20clean%20white%20background%2C%20premium%20transfer%20service%2C%20modern%20design%2C%20spacious%20interior&width=400&height=300&seq=suv-car&orientation=landscape',
      features: [t('fleetFeature1'), t('fleetFeature2'), t('fleetFeature6'), t('fleetFeature7')],
      price: applyWinterDiscount(60),
      originalPrice: 60,
      example: t('fleetExample3'),
      route: 'Aeroporto Faro - Carvoeiro'
    },
    {
      name: t('fleetMPV'),
      passengers: 5,
      luggage: 5,
      description: t('fleetMPVDesc'),
      image: 'https://readdy.ai/api/search-image?query=Modern%20MPV%20minivan%20in%20orange%20and%20gray%20colors%20near%20Faro%20Airport%2C%20professional%20automotive%20photography%2C%20clean%20white%20background%2C%20spacious%20family%20vehicle%2C%20large%20luggage%20capacity%2C%20premium%20transfer%20service&width=400&height=300&seq=mpv-car&orientation=landscape',
      features: [t('fleetFeature1'), t('fleetFeature2'), t('fleetFeature8'), t('fleetFeature9')],
      price: applyWinterDiscount(65),
      originalPrice: 65,
      example: t('fleetExample4'),
      route: 'Aeroporto Faro - Tavira'
    },
    {
      name: t('fleetMinivan'),
      passengers: 6,
      luggage: 6,
      description: t('fleetMinivanDesc'),
      image: 'https://readdy.ai/api/search-image?query=Large%20minivan%20vehicle%20in%20orange%20and%20gray%20colors%20at%20Algarve%20tourist%20destination%2C%20professional%20automotive%20photography%2C%20clean%20white%20background%2C%20group%20transfer%20vehicle%2C%206%20passenger%20capacity%2C%20spacious%20interior&width=400&height=300&seq=minivan-car&orientation=landscape',
      features: [t('fleetFeature1'), t('fleetFeature2'), t('fleetFeature10'), t('fleetFeature11')],
      price: applyWinterDiscount(100),
      originalPrice: 100,
      example: t('fleetExample5'),
      route: 'Aeroporto Faro - Portimão'
    },
    {
      name: t('fleetMinibus'),
      passengers: 8,
      luggage: 16,
      description: t('fleetMinibusDesc'),
      image: 'https://readdy.ai/api/search-image?query=Professional%20minibus%20vehicle%20in%20orange%20and%20gray%20colors%20for%20group%20transfers%2C%20professional%20automotive%20photography%2C%20clean%20white%20background%2C%208%20passenger%20capacity%2C%20large%20luggage%20space%2C%20premium%20group%20transport&width=400&height=300&seq=minibus-car&orientation=landscape',
      features: [t('fleetFeature1'), t('fleetFeature2'), t('fleetFeature7'), t('fleetFeature12')],
      price: applyWinterDiscount(140),
      originalPrice: 140,
      example: t('fleetExample6'),
      route: 'Aeroporto Faro - Lagos'
    }
  ];

  const handleBookRoute = (route: string) => {
    // Navegar para a página de tarifário com a rota selecionada
    window.REACT_APP_NAVIGATE(`/pricing?route=${encodeURIComponent(route)}`);
  };

  return (
    <section id="frota" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            {t('ourFleet')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('fleetDescription')}
          </p>
          {winterSeason && (
            <div className="mt-6 inline-block bg-blue-50 border-2 border-blue-200 rounded-xl px-6 py-3">
              <div className="flex items-center space-x-2">
                <i className="ri-snowflake-line text-2xl text-blue-600"></i>
                <div className="text-left">
                  <div className="text-base font-bold text-blue-900">{t('winterDiscount')}</div>
                  <div className="text-xs text-blue-700">{t('winterDiscountActive')}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {fleetCategories.map((vehicle, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <img 
                src={vehicle.image} 
                alt={vehicle.name}
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{vehicle.name}</h3>
                <p className="text-gray-600 mb-4">{vehicle.description}</p>
                <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
                  <span className="flex items-center">
                    <i className="ri-user-line mr-1"></i>
                    {vehicle.passengers} {t('fleetPassengers')}
                  </span>
                  <span className="flex items-center">
                    <i className="ri-suitcase-line mr-1"></i>
                    {vehicle.luggage} {t('luggage')}
                  </span>
                </div>
                <div className="space-y-2 mb-4">
                  {vehicle.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-orange-500 mr-2"></i>
                      {feature}
                    </div>
                  ))}
                </div>
                <div className="bg-orange-50 rounded-lg p-3 mb-4">
                  <div className="flex items-center text-sm text-gray-700">
                    <i className="ri-map-pin-line text-orange-500 mr-2"></i>
                    <span className="font-medium">{vehicle.example}</span>
                  </div>
                </div>
                <div className="text-center mb-4">
                  {winterSeason && (
                    <div className="text-sm text-gray-400 line-through mb-1">€{vehicle.originalPrice}</div>
                  )}
                  <div className="flex items-center justify-center">
                    <span className={`text-2xl font-bold ${winterSeason ? 'text-blue-600' : 'text-orange-500'}`}>
                      €{vehicle.price}
                    </span>
                    <span className="text-gray-500 text-sm ml-1">/ transfer</span>
                  </div>
                  {winterSeason && (
                    <div className="text-xs text-blue-600 mt-1">
                      <i className="ri-snowflake-line"></i> Desconto de inverno aplicado
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleBookRoute(vehicle.route)}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('bookThisRoute')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
